import { createSolanaClient } from '../client'
import type { Client, HeadersInput } from '../client/create'

type GetSolanaAccountSignaturesParameters = {
  rpcEndpoints: readonly string[]
  accountAddress: string
  from: number | undefined
  to: number | undefined
  headers?: HeadersInput
  timeout?: number
}

type SignatureWithTimestamp = {
  signature: string
  timestamp: number
}

type GetSolanaAccountSignaturesReturnType = {
  signatures: readonly SignatureWithTimestamp[]
  tokenAccountAddresses: readonly string[]
  tokenAccountMintMap: { [account: string]: string | undefined }
  tokenAccountDecimalMap: { [account: string]: number | undefined }
}

export const getSolanaAccountSignatures = async ({
  rpcEndpoints,
  accountAddress,
  from = 0,
  to = Date.now(),
  headers,
  timeout,
}: GetSolanaAccountSignaturesParameters): Promise<GetSolanaAccountSignaturesReturnType> => {
  const client = createSolanaClient({ rpcEndpoints })

  // token accounts を取得
  const tokenAccounts = await client.getTokenAccountsByOwner({
    ownerPubKey: accountAddress,
    headers,
    timeout,
  })

  // account 毎に transaction signatures を取得
  const accountAddresses = [
    ...new Set([
      accountAddress,
      ...tokenAccounts.map((account) => account.pubkey),
    ]),
  ]

  // 重複発生しないように signature 取得
  const signaturesMap = new Map<string, SignatureWithTimestamp>()
  await Promise.all(
    accountAddresses.map((address) =>
      (async () => {
        const signatures = await getAllSignaturesForAddress({
          client,
          address,
          from,
          to,
          headers,
          timeout,
        })
        for (const data of signatures) {
          if (signaturesMap.has(data.signature)) {
            continue
          }
          signaturesMap.set(data.signature, data)
        }
      })(),
    ),
  )
  const signaturesWithTimestamp = [...signaturesMap.values()]

  // token account の mint, decimal を取得
  const tokenAccountMintMap: { [account: string]: string | undefined } = {}
  const tokenAccountDecimalMap: { [account: string]: number | undefined } = {}
  for (const account of tokenAccounts) {
    tokenAccountMintMap[account.pubkey] = account.account.data.parsed.info.mint
    tokenAccountDecimalMap[account.pubkey] =
      account.account.data.parsed.info.tokenAmount.decimals
  }

  return {
    signatures: signaturesWithTimestamp,
    tokenAccountAddresses: tokenAccounts.map((account) => account.pubkey),
    tokenAccountMintMap,
    tokenAccountDecimalMap,
  }
}

type GetAllSignaturesForAddressParameters = {
  client: Client
  address: string
  from: number
  to: number
  headers?: HeadersInput
  timeout?: number
}

const limit = 1000

const getAllSignaturesForAddress = async ({
  client,
  address,
  from,
  to,
  headers,
  timeout,
}: GetAllSignaturesForAddressParameters): Promise<
  readonly SignatureWithTimestamp[]
> => {
  const signaturesWithTimestamp: SignatureWithTimestamp[] = []
  const repeat = true
  let before: string | undefined
  while (repeat) {
    const signatures = await client.getSignaturesForAddress({
      address,
      before,
      headers,
      timeout,
    })
    // blockTime で降順ソート
    const sortedSignatures = signatures
      .filter((signature) => signature.blockTime !== null)
      .toSorted((a, b) => (b.blockTime ?? 0) - (a.blockTime ?? 0))
    if (sortedSignatures.length === 0) {
      break
    }

    // blockTime が from < blockTime < to の範囲の signature を追加
    for (const signature of sortedSignatures) {
      if (signature.blockTime === null) {
        continue
      }
      const timestamp = signature.blockTime * 1000
      if (timestamp > to) {
        continue
      }
      // blockTime が from より小さい場合は範囲外に出たので取得終了
      if (timestamp < from) {
        break
      }
      signaturesWithTimestamp.push({
        signature: signature.signature,
        timestamp,
      })
    }

    // signature が limit 未満なら終了
    if (signatures.length < limit) {
      break
    }

    // before を更新
    before = sortedSignatures[sortedSignatures.length - 1].signature
  }
  return signaturesWithTimestamp
}
